import React, { useState, useEffect } from 'react';
import { CalendarDays, DollarSign, TrendingDown, TrendingUp, Eye, X } from 'lucide-react';
import { base_url, getToken } from '../../config/config';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

// Custom Components
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg shadow-md ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children, className = "" }) => (
  <div className={`p-6 pb-4 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = "" }) => (
  <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>
    {children}
  </h3>
);

const CardDescription = ({ children, className = "" }) => (
  <p className={`text-sm text-gray-600 mt-1 ${className}`}>
    {children}
  </p>
);

const CardContent = ({ children, className = "" }) => (
  <div className={`p-6 pt-0 ${className}`}>
    {children}
  </div>
);

function Badge({
  children,
  variant = "default",
}) {
  const base = "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
  const variants = {
    default: "bg-green-100 text-green-800",
    destructive: "bg-red-100 text-red-800",
    secondary: "bg-gray-100 text-gray-800",
  }
  return <span className={`${base} ${variants[variant]}`}>{children}</span>
}

const Button = ({ children, variant = "default", size = "default", onClick, className = "", disabled = false }) => {
  const variants = {
    default: "bg-blue-600 hover:bg-blue-700 text-white",
    outline: "border border-gray-300 hover:bg-gray-50 text-gray-700",
    destructive: "bg-red-600 hover:bg-red-700 text-white"
  };
  
  const sizes = {
    default: "px-4 py-2 text-sm",
    sm: "px-3 py-1.5 text-xs",
    lg: "px-6 py-3 text-base"
  };
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {children}
    </button>
  );
};


function ProgressBar({ value }) {
  return (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className="h-2 rounded-full bg-blue-600"
        style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }}
      />
    </div>
  )
}

const Table = ({ children, className = "" }) => (
  <div className={`overflow-x-auto ${className}`}>
    <table className="min-w-full divide-y divide-gray-200">
      {children}
    </table>
  </div>
);

const TableHeader = ({ children }) => (
  <thead className="bg-gray-50">
    {children}
  </thead>
);

const TableBody = ({ children }) => (
  <tbody className="bg-white divide-y divide-gray-200">
    {children}
  </tbody>
);

const TableRow = ({ children, className = "" }) => (
  <tr className={`hover:bg-gray-50 ${className}`}>
    {children}
  </tr>
);

const TableHead = ({ children, className = "" }) => (
  <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${className}`}>
    {children}
  </th>
);

const TableCell = ({ children, className = "" }) => (
  <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${className}`}>
    {children}
  </td>
);

const Progress = ({ value, className = "" }) => (
  <div className={`w-full bg-gray-200 rounded-full ${className}`}>
    <div 
      className="bg-blue-600 h-full rounded-full transition-all duration-300"
      style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }}
    />
  </div>
);

const Modal = ({ isOpen, onClose, title, description, children }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl max-w-4xl max-h-[80vh] w-full mx-4 overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {description && <p className="text-sm text-gray-600 mt-1">{description}</p>}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

// Data types and mock data
const mockANRRecords = [
  {
    id: "ANR-001",
    name: "Project Alpha",
    totalCost: 15000,
    plannedCost: 12000,
    profitLoss: -3000,
    status: "loss",
    date: "2024-01-15",
    processes: [
      {
        id: "P1",
        label: "Design Phase",
        scheduledHours: 40,
        actualHours: 45,
        plannedLabourCost: 2000,
        actualLabourCost: 2250,
        costPerHour: 50,
        status: "completed",
      },
      {
        id: "P2",
        label: "Development",
        scheduledHours: 80,
        actualHours: 95,
        plannedLabourCost: 4000,
        actualLabourCost: 4750,
        costPerHour: 50,
        status: "completed",
      },
      {
        id: "P3",
        label: "Testing",
        scheduledHours: 30,
        actualHours: 35,
        plannedLabourCost: 1500,
        actualLabourCost: 1750,
        costPerHour: 50,
        status: "pending",
      },
    ],
  },
  {
    id: "ANR-002",
    name: "Project Beta",
    totalCost: 8000,
    plannedCost: 10000,
    profitLoss: 2000,
    status: "profit",
    date: "2024-01-20",
    processes: [
      {
        id: "P4",
        label: "Planning",
        scheduledHours: 20,
        actualHours: 18,
        plannedLabourCost: 1000,
        actualLabourCost: 900,
        costPerHour: 50,
        status: "completed",
      },
      {
        id: "P5",
        label: "Implementation",
        scheduledHours: 60,
        actualHours: 55,
        plannedLabourCost: 3000,
        actualLabourCost: 2750,
        costPerHour: 50,
        status: "completed",
      },
    ],
  },
  {
    id: "ANR-003",
    name: "Project Gamma",
    totalCost: 12000,
    plannedCost: 11000,
    profitLoss: -1000,
    status: "loss",
    date: "2024-02-01",
    processes: [
      {
        id: "P6",
        label: "Research",
        scheduledHours: 25,
        actualHours: 30,
        plannedLabourCost: 1250,
        actualLabourCost: 1500,
        costPerHour: 50,
        status: "completed",
      },
      {
        id: "P7",
        label: "Development",
        scheduledHours: 70,
        actualHours: 75,
        plannedLabourCost: 3500,
        actualLabourCost: 3750,
        costPerHour: 50,
        status: "pending",
      },
    ],
  },
  {
    id: "ANR-004",
    name: "Project Delta",
    totalCost: 6000,
    plannedCost: 8000,
    profitLoss: 2000,
    status: "profit",
    date: "2024-02-10",
    processes: [
      {
        id: "P8",
        label: "Analysis",
        scheduledHours: 30,
        actualHours: 25,
        plannedLabourCost: 1500,
        actualLabourCost: 1250,
        costPerHour: 50,
        status: "completed",
      },
    ],
  },
];

const profitLossOverTime = [
  { date: "Jan 15", profit: -3000, loss: 3000 },
  { date: "Jan 20", profit: 2000, loss: 0 },
  { date: "Feb 01", profit: 0, loss: 1000 },
  { date: "Feb 10", profit: 2000, loss: 0 },
];

const profitLossDistribution = [
  { name: "Profit", value: 4000, color: "#22c55e" },
  { name: "Loss", value: 4000, color: "#ef4444" },
];

export default function ProfitLossPage() {
  const [selectedANR, setSelectedANR] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [anrRecords, setAnrRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch ANR profit data from API
  useEffect(() => {
    const fetchANRProfit = async () => {
      try {
        setLoading(true);
        const token = getToken();
        const response = await fetch(`${base_url}/v1/getANRProfit`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        // Transform API data to match our component structure
        const transformedData = Array.isArray(data) ? data.map(transformANRData) : [transformANRData(data)];
        setAnrRecords(transformedData);
      } catch (err) {
        console.error('Error fetching ANR profit data:', err);
        setError(err.message);
        // Fallback to mock data if API fails
        setAnrRecords(mockANRRecords);
      } finally {
        setLoading(false);
      }
    };

    fetchANRProfit();
  }, []);

  // Transform API response to match component structure
  const transformANRData = (apiData) => {
    return {
      id: apiData.anr,
      totalCost: apiData.totalLabourCost || 0,
      plannedCost: apiData.processes?.reduce((sum, p) => sum + (p.plannedLabourCost || 0), 0) || 0,
      profitLoss: apiData.profitOrLoss || 0,
      status: apiData.isProfit ? "profit" : "loss",
      date: new Date().toISOString().split('T')[0], // Using current date as fallback
      auftragswertNetto: apiData.auftragswertNetto || 0,
      processes: apiData.processes?.map(process => ({
        id: `${process.anr}-${process.label}`,
        label: process.label,
        scheduledHours: process.totalHours || 0,
        actualHours: process.actualHours || 0,
        plannedLabourCost: process.plannedLabourCost || 0,
        actualLabourCost: process.actualLabourCost || 0,
        costPerHour: process.mengeCost || 0,
        status: process.isCompleted ? "completed" : "pending",
        overworked: process.overworked || false,
      })) || [],
    };
  };

  const totalProfit = anrRecords
    .filter((record) => record.status === "profit")
    .reduce((sum, record) => sum + record.profitLoss, 0);

  const totalLoss = anrRecords
    .filter((record) => record.status === "loss")
    .reduce((sum, record) => sum + Math.abs(record.profitLoss), 0);

  const netProfitLoss = totalProfit - totalLoss;

  // Generate chart data from actual data
  const profitLossOverTime = anrRecords.map((record, index) => ({
    date: new Date(record.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    profit: record.status === 'profit' ? record.profitLoss : 0,
    loss: record.status === 'loss' ? Math.abs(record.profitLoss) : 0,
  }));

  const profitLossDistribution = [
    { name: "Profit", value: totalProfit, color: "#22c55e" },
    { name: "Loss", value: totalLoss, color: "#ef4444" },
  ];

  const handleViewDetails = (record) => {
    setSelectedANR(record);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedANR(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Profit & Loss Dashboard</h1>
            <p className="text-gray-600 mt-1">ANR Records Analysis and Financial Overview</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <CalendarDays className="h-4 w-4" />
            <span>Last updated: {new Date().toLocaleDateString()}</span>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <span className="ml-4 text-gray-600">Loading ANR profit data...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-6">
              <div className="flex items-center text-red-800">
                <X className="h-5 w-5 mr-2" />
                <span>Error loading data: {error}</span>
              </div>
              <p className="text-red-600 text-sm mt-2">Displaying fallback data for demonstration.</p>
            </CardContent>
          </Card>
        )}

        {/* Content - only show when not loading */}
        {!loading && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Profit</CardTitle>
                  <TrendingUp className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">${totalProfit.toLocaleString()}</div>
                  <p className="text-xs text-gray-600 mt-1">From profitable ANRs</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Loss</CardTitle>
                  <TrendingDown className="h-4 w-4 text-red-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">${totalLoss.toLocaleString()}</div>
                  <p className="text-xs text-gray-600 mt-1">From loss-making ANRs</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Net P&L</CardTitle>
                  <DollarSign className={`h-4 w-4 ${netProfitLoss >= 0 ? "text-green-600" : "text-red-600"}`} />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${netProfitLoss >= 0 ? "text-green-600" : "text-red-600"}`}>
                    ${Math.abs(netProfitLoss).toLocaleString()}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{netProfitLoss >= 0 ? "Net Profit" : "Net Loss"}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total ANRs</CardTitle>
                  <div className="h-4 w-4 bg-blue-600 rounded" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{anrRecords.length}</div>
                  <p className="text-xs text-gray-600 mt-1">Active records</p>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Profit & Loss Trend</CardTitle>
                  <CardDescription>Financial performance over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={profitLossOverTime}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip
                        formatter={(value, name) => [
                          `$${Math.abs(Number(value)).toLocaleString()}`,
                          name === "profit" ? "Profit" : "Loss",
                        ]}
                      />
                      <Line type="monotone" dataKey="profit" stroke="#22c55e" strokeWidth={2} />
                      <Line type="monotone" dataKey="loss" stroke="#ef4444" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Profit vs Loss Distribution</CardTitle>
                  <CardDescription>Overall financial breakdown</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={profitLossDistribution}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, value }) => `${name}: $${value.toLocaleString()}`}
                      >
                        {profitLossDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`$${Number(value).toLocaleString()}`, "Amount"]} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* ANR Records Table */}
            <Card>
              <CardHeader>
                <CardTitle>ANR Records</CardTitle>
                <CardDescription>Detailed view of all Analysis and Review records</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ANR ID</TableHead>
                      {/* <TableHead>Project Name</TableHead> */}
                      <TableHead>Total Cost</TableHead>
                      <TableHead>Planned Cost</TableHead>
                      <TableHead>Order Value</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>P&L Amount</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {anrRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell className="font-medium">{record.id}</TableCell>
                        <TableCell>${record.totalCost.toLocaleString()}</TableCell>
                        <TableCell>${record.plannedCost.toLocaleString()}</TableCell>
                        <TableCell>${record.auftragswertNetto?.toLocaleString() || 'N/A'}</TableCell>
                        <TableCell>
                          <Badge
                            variant={record.status === "profit" ? "default" : "destructive"}
                            className={record.status === "profit" ? "bg-green-100 text-green-800 hover:bg-green-200" : ""}
                          >
                            {record.status === "profit" ? (
                              <>
                                <TrendingUp className="h-3 w-3 mr-1" /> Profit
                              </>
                            ) : (
                              <>
                                <TrendingDown className="h-3 w-3 mr-1" /> Loss
                              </>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`font-semibold ${record.status === "profit" ? "text-green-600" : "text-red-600"}`}
                          >
                            {record.status === "profit" ? "+" : "-"}${Math.abs(record.profitLoss).toLocaleString()}
                          </span>
                        </TableCell>
                        <TableCell>{new Date(record.date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleViewDetails(record)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}

        {/* Modal for ANR Details */}
        <Modal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          title={selectedANR ? `ANR Details - ${selectedANR.name}` : ""}
          description={selectedANR ? `Detailed process breakdown for ${selectedANR.id}` : ""}
        >
           {selectedANR && (
            <div className="space-y-6">
              {/* Summary boxes */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {/* Total Cost */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-600">Total Cost</div>
                  <div className="text-lg font-semibold">${selectedANR.totalCost.toLocaleString()}</div>
                </div>
                {/* Planned Cost */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-600">Planned Cost</div>
                  <div className="text-lg font-semibold">${selectedANR.plannedCost.toLocaleString()}</div>
                </div>
                {/* Order Value */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-600">Order Value</div>
                  <div className="text-lg font-semibold">${selectedANR.auftragswertNetto?.toLocaleString() || 'N/A'}</div>
                </div>
                {/* Variance */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-600">Variance</div>
                  <div className={`text-lg font-semibold ${selectedANR.status==='profit'?'text-green-600':'text-red-600'}`}>{selectedANR.status==='profit'?'+':'-'}$
                  {Math.abs(selectedANR.profitLoss).toLocaleString()}</div>
                </div>
                {/* Process Count */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-600">Processes</div>
                  <div className="text-lg font-semibold">{selectedANR.processes.length}</div>
                </div>
              </div>

              {/* Process List */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Process Breakdown</h3>
                <div className="space-y-4">
                  {selectedANR.processes.map(process => (
                    <Card key={process.id} className="p-6">
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium">{process.label}</h4>
                          <div className="flex gap-2">
                            <Badge variant={process.status==='completed'?'default':'secondary'}>
                              {process.status}
                            </Badge>
                            {process.overworked && (
                              <Badge variant="destructive">
                                Overworked
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div><div className="text-gray-600">Scheduled Hours</div><div className="font-medium">{process.scheduledHours}h</div></div>
                          <div><div className="text-gray-600">Actual Hours</div><div className="font-medium">{process.actualHours}h</div></div>
                          <div><div className="text-gray-600">Planned Cost</div><div className="font-medium">${process.plannedLabourCost.toLocaleString()}</div></div>
                          <div><div className="text-gray-600">Actual Cost</div><div className="font-medium">${process.actualLabourCost.toLocaleString()}</div></div>
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="text-sm"><span className="text-gray-600">Cost per hour:</span> <span className="font-medium">${process.costPerHour}</span></div>
                          <div className="text-sm"><span className="text-gray-600">Variance:</span> <span className={`font-medium ${process.actualLabourCost <= process.plannedLabourCost?'text-green-600':'text-red-600'}`}>{process.actualLabourCost <= process.plannedLabourCost?'-': '+'}${Math.abs(process.actualLabourCost - process.plannedLabourCost).toLocaleString()}</span></div>
                        </div>
                        <div className="mt-3">
                          <div className="flex justify-between text-xs text-gray-600 mb-1"><span>Hours Progress</span><span>{process.actualHours}/{process.scheduledHours}h</span></div>
                          <ProgressBar value={(process.actualHours / process.scheduledHours) * 100} />
                        </div>
                        {process.overworked && (
                          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded">
                            <div className="text-sm text-red-800">
                              <strong>Warning:</strong> This process exceeded planned hours by {process.actualHours - process.scheduledHours} hours
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
}